import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  show as intercomShow,
  hide as intercomHide,
  onShow as intercomOnShow,
  onHide as intercomOnHide,
} from "@intercom/messenger-js-sdk";
import { useUI } from "state";
import { useFeatureFlag } from "state/FeaturesFlagContext";
import FEATURE_FLAGS from "state/FeaturesFlagContext/flags";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { getEventId } from "Router";
import {
  XICon,
  SupportChatIcon,
  AIFlightAgentIcon,
  QuestionBoxIcon,
} from "components/icons";
import AiChat from "AiChat";
import openToast from "utils/openToast";

const MENU_ITEM_CLASS =
  "w-full flex items-center gap-2 px-5 py-3 text-left text-white text-sm transition-colors hover:bg-white/10 cursor-pointer";

type IntercomFn = (action: string, data?: object) => void;

function getIntercom(): IntercomFn | undefined {
  const fn = (globalThis as unknown as { Intercom?: IntercomFn }).Intercom;
  return typeof fn === "function" ? fn : undefined;
}

function getFullPageUrl(): string {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

function syncIntercomSource(
  pathname: string,
  eventId: string | number | undefined,
) {
  const intercom = getIntercom();

  if (!intercom) return;

  const eventIdStr =
    eventId != null && String(eventId).trim() !== "" ? String(eventId) : null;

  intercom("update", {
    custom_attributes: {
      source: getFullPageUrl(),
      source_page: pathname,
      ...(eventIdStr && { source_event_id: eventIdStr }),
    },
  });
}

export default function SupportChatLauncher() {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isIntercomOpen, setIsIntercomOpen] = useState(false);
  const { openRightDrawer, closeRightDrawer } = useUI();
  const { intercom } = useEnvs((state) => state);
  const isAIChatEnabled = useFeatureFlag(FEATURE_FLAGS.aiChatbot);
  const eventID = getEventId();
  const showSupportChat = Boolean(intercom);
  const showAIFlightAgent = isAIChatEnabled && Boolean(eventID);
  const hasBothLaunchers = showSupportChat && showAIFlightAgent;

  useEffect(() => {
    if (!intercom) return;

    try {
      intercomOnShow(() => setIsIntercomOpen(true));
      intercomOnHide(() => setIsIntercomOpen(false));
      syncIntercomSource(pathname, eventID);
    } catch {
      openToast(
        "Support chat may not sync correctly. Refresh the page if needed.",
        "error",
      );
    }
  }, [intercom, pathname, eventID]);

  const openAIChat = useCallback(() => {
    setIsOpen(false);

    openRightDrawer({
      content: <AiChat eventID={eventID} />,
      isClosableByOverlay: true,
    });
  }, [eventID, openRightDrawer]);

  const openIntercom = useCallback(() => {
    if (!intercom) return;
    setIsOpen(false);
    try {
      if (isIntercomOpen) intercomHide();
      else intercomShow();
    } catch {
      openToast("Support chat couldn't open. Please try again.", "error");
    }
  }, [intercom, isIntercomOpen]);

  const handlePrimaryClick = useCallback(() => {
    if (showSupportChat && !showAIFlightAgent) {
      openIntercom();
      return;
    }

    if (showAIFlightAgent && !showSupportChat) {
      openAIChat();
      return;
    }

    setIsOpen((prev) => {
      const next = !prev;

      if (next) {
        if (isIntercomOpen) {
          intercomHide();
        }

        closeRightDrawer();
      }

      return next;
    });
  }, [
    closeRightDrawer,
    isIntercomOpen,
    openAIChat,
    openIntercom,
    showAIFlightAgent,
    showSupportChat,
  ]);

  if (!showSupportChat && !showAIFlightAgent) {
    return null;
  }

  return (
    <div className="fixed bottom-5 right-8 z-[50] flex flex-col items-end gap-3">
      {hasBothLaunchers && isOpen && (
        <div className="flex min-w-[220px] flex-col border border-[rgba(128,128,128,0.55)] bg-[#2E2E2E]">
          {showSupportChat && (
            <button
              type="button"
              onClick={openIntercom}
              className={MENU_ITEM_CLASS}
            >
              <SupportChatIcon />
              <span>Support Chat</span>
            </button>
          )}

          {showAIFlightAgent && (
            <button
              type="button"
              onClick={openAIChat}
              className={MENU_ITEM_CLASS}
            >
              <AIFlightAgentIcon />
              <span>AI Flight Assistant</span>
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handlePrimaryClick}
        className="flex items-center justify-center rounded-full border border-[#8C8C8C] bg-[#2E2E2E] p-3 transition-colors hover:opacity-90 cursor-pointer"
      >
        {hasBothLaunchers ? (
          isOpen ? (
            <XICon fill="#E4E4E4" />
          ) : (
            <QuestionBoxIcon />
          )
        ) : showSupportChat ? (
          <SupportChatIcon />
        ) : (
          <AIFlightAgentIcon />
        )}
      </button>
    </div>
  );
}
